// Case Buddy Tool 68B processor adapter.
// This module ports Tool68BPage.vue's raw-data preparation pipeline into a
// non-visual helper used by the Case Buddy Web App.
// Spreadsheet-shaped rows remain the source of truth.

const tool68BMethods = {

    processData() {
      this.resetStatus();

      let data = this.$refs.inputSpreadsheet.getData() || [];
      data = this.clone2D(data);

      // Step 40 (Deduplication + Notes)
      if (this.chain === "BTC") {
        data = this.runTool40F_All(data);
      } else {
        data = this.runTool40D_All(data);
      }

      // Step 49B (Continuation Fixer)
      data = this.runTool49B_All(data);

      // Step 46B (Path Label Generator)
      data = this.runTool46B_All(data);

      // Step 50 / 50B (Vertical Arrangement)
      const { data: arrangedData, missingLabels } = this.runTool50_All(data);

      if (missingLabels.length > 0) {
        this.missingLabels = missingLabels;
        // Still output the labeled (but unsorted) data so the user can inspect/fix.
        this.$refs.outputSpreadsheet.loadData(data);
        return;
      }

      this.$refs.outputSpreadsheet.loadData(arrangedData);

      // Final message (keep any earlier feedback and append)
      const finalLine = "✅ Workflow complete. Final output is in Spreadsheet 2.";
      this.userFeedback = this.userFeedback
        ? `${this.userFeedback}\n${finalLine}`
        : finalLine;
    },

    resetStatus() {
      this.duplicateCount = 0;
      this.notesUpdated = 0;
      this.tokenMismatchCount = 0;
      this.userFeedback = "";
      this.changesLog = [];
      this.missingLabels = [];

      // Tool46B internal state
      this.hashes = new Set();
      this.originHash = null;
    },

    clearData() {
      this.resetStatus();
     //   const blank = Array(100)
      //    .fill()
      //    .map(() => Array(17).fill(""));
   //   this.$refs.inputSpreadsheet.loadData(blank);
   //   this.$refs.outputSpreadsheet.loadData(blank);
            
          this.$refs.inputSpreadsheet.loadData(Array(100).fill().map(() => [""]));
          this.$refs.outputSpreadsheet.loadData(Array(100).fill().map(() => [""]));
     

    },

    clone2D(data) {
      return (data || []).map((row) => (Array.isArray(row) ? [...row] : []));
    },

    runTool40D_All(inputData) {
      const processedData = this.removeDuplicatesWithNotes(inputData);
      const finalData = this.addEmptyRowsAfterNotes(processedData);
      return this.checkNotes_ETH(finalData);
    },

    checkNotes_ETH(data) {
      let updates = 0;
      let mismatches = 0;

      const bracketRegex = /\(([\d,]+\.\d{2})\s+([A-Z]+)\)/;

      data.forEach((row) => {
        const note = row[9];
        const fundsFollowed = row[12];

        if (!note || !fundsFollowed) return;

        if (note.includes("A portion of")) {
          const match = note.match(bracketRegex);
          if (match) {
            const [, bracketAmount, bracketToken] = match;
            const [fundsAmountRaw, fundsTokenRaw] = fundsFollowed.trim().split(" ");

            const fundsAmount = fundsAmountRaw.replace(/,/g, "");
            const newAmount = `${fundsAmount} ${fundsTokenRaw}`;

            if (fundsTokenRaw !== bracketToken) mismatches++;

            if (newAmount !== `${bracketAmount} ${bracketToken}`) {
              const updatedNote = note.replace(bracketRegex, `(${newAmount})`);
              row[9] = updatedNote;
              updates++;
            }
          }
        }
      });

      this.notesUpdated = updates;
      this.tokenMismatchCount = mismatches;

      if (updates > 0) {
        let msg = `✅ Updated ${updates} notes to match funds followed.`;
        if (mismatches > 0)
          msg += ` ⚠️ ${mismatches} token mismatches detected. Please review.`;
        this.userFeedback = msg;
      } else {
        let msg = `✅ No notes needed updating.`;
        if (mismatches > 0)
          msg += ` ⚠️ ${mismatches} token mismatches detected. Please review.`;
        this.userFeedback = msg;
      }

      return data;
    },

    runTool40F_All(inputData) {
      const processedData = this.removeBitcoinDuplicates(inputData);
      const cleanedData = this.cleanupEmptyRows(processedData);
      return this.checkNotes_BTC(cleanedData);
    },

    checkNotes_BTC(data) {
      let updates = 0;

      data.forEach((row) => {
        const note = String(row[9] || "");
        const fundsFollowed = String(row[12] || "").trim();

        if (!note) return;

        let updatedNote = note;
        const [followedAmountRaw = "", followedTokenRaw = ""] = fundsFollowed.split(/\s+/, 2);
        const followedValue = [followedAmountRaw, followedTokenRaw].filter(Boolean).join(" ");

        if (fundsFollowed && note.includes("Follows")) {
          const followsRegex = /\(Follows [^)]+\)/;
          updatedNote = updatedNote.replace(followsRegex, `(Follows ${followedValue})`);
        }

        if (note.includes("A portion of")) {
          const dormantFundsRegex = /\(([\d,]+(?:\.\d+)?)(?:\s+([A-Z]+))?\)/;
          updatedNote = updatedNote.replace(
            dormantFundsRegex,
            (_, amount, tokenInNote) => `(${amount} ${tokenInNote || followedTokenRaw || "BTC"})`
          );
        }

        if (updatedNote !== note) {
          row[9] = updatedNote;
          updates++;
        }
      });

      this.notesUpdated = updates;

      // Keep user feedback consistent across chains
      if (updates > 0) {
        this.userFeedback = `✅ Updated ${updates} notes to match funds followed.`;
      } else if (!this.userFeedback) {
        this.userFeedback = `✅ No notes needed updating.`;
      }

      return data;
    },

    runTool49B_All(inputData) {
      this.changesLog = [];

      // Remove header row if it looks like one
      let data = inputData;
      const firstRow = data[0] || [];
      const hasHeader =
        firstRow[0]?.trim() === "Label" &&
        firstRow[1]?.trim() === "Hash" &&
        firstRow[2]?.trim()?.toLowerCase().includes("from");

      if (hasHeader) {
        data = data.slice(1);
        this.changesLog.push("Removed header row automatically.");
      }

      let outputData = this.fixContinuationData(data);

      // Prune unfollowed BTC outputs (Tool49B default behavior)
      if (this.pruneBTCUnfollowed) {
        const originalLength = outputData.length;

        outputData = outputData.filter((row) => {
          const isEmptyABC = !row[0]?.trim() && !row[1]?.trim() && !row[2]?.trim();
          const hasToAddress = row[3]?.trim();
          const isEmptyM = !row[12]?.trim();
          const isBTC = row[6]?.trim() === "BTC";
          return !(isEmptyABC && hasToAddress && isEmptyM && isBTC);
        });

        const removedCount = originalLength - outputData.length;
        if (removedCount > 0) {
          this.changesLog.push(
            `Removed ${removedCount} extra BTC outputs that matched pruning criteria.`
          );
        }
      }

      // Additional pruning: carry Hash + FromAddress into the next BTC child row, then remove the empty-funds parent row.
      if (this.pruneBTCUnfollowed) {
        const updatedOutput = [];

        for (let i = 0; i < outputData.length; i++) {
          const current = outputData[i];
          const next = outputData[i + 1];

          const hasHashAndFrom = current[1]?.trim() && current[2]?.trim();
          const isBTC = current[6]?.trim() === "BTC";
          const noFundsFollowed = !current[12]?.trim();

          const nextIsValidBTCChild =
            next &&
            !next[1]?.trim() &&
            !next[2]?.trim() &&
            next[6]?.trim() === "BTC" &&
            next[12]?.trim();

          if (hasHashAndFrom && isBTC && noFundsFollowed && nextIsValidBTCChild) {
            // Fill next row with Hash and FromAddress
            next[1] = current[1]; // Copy Hash
            next[2] = current[2]; // Copy FromAddress

            this.changesLog.push(
              `Removed row with BTC hash ${current[1]} that had no funds followed, but carried hash/FromAddress into next row.`
            );

            // Skip adding current row to updatedOutput
            continue;
          }

          updatedOutput.push(current);
        }

        outputData = updatedOutput;
      }

      return outputData;
    },

    runTool46B_All(inputData) {
      const originalData = inputData;
      const data = this.chain === "BTC" ? this.clone2D(inputData) : inputData;

      // BTC entries can carry output-specific Column Q data on continuation rows.
      // Tool 46B's graph labelling code reads Column Q from the row that has Column B,
      // so use a temporary labelling copy with entry-level child hashes promoted to the
      // hash row. The original output-specific Column Q cells are copied back untouched.
      if (this.chain === "BTC") {
        this.promoteBitcoinEntryContinuationDataForLabeling(data);
      }

      // Step 1: Collect hashes
      const { allHashes, originHash } = this.collectHashes(data);
      if (!originHash) {
        console.error(
          "No origin hash found. Ensure Column B contains at least one valid transaction hash."
        );
        this.userFeedback = this.userFeedback
          ? `${this.userFeedback}\n❌ No origin hash found (Tool 46B).`
          : "❌ No origin hash found (Tool 46B).";
        return originalData;
      }

      // Step 2: Label the longest path or the origin
      if (this.labelLongestPath) {
        this.labelLongestPathWithSimpleLabels(data, originHash, allHashes);
        console.log("Labelled longest path, moving on.");
      } else {
        this.labelOriginWithBaseLabel(data, originHash);
        console.log("Labelled origin with base label.");
      }

      // Step 3: Label remaining paths
      this.labelRemainingPaths(data, originHash, allHashes);

      // Step 4: Handle subsequent branching
      this.handleSubsequentBranching(
        new Set(data.map((row) => row[0]?.trim()).filter(Boolean)), // usedLabels
        new Map(data.map((row) => [row[1]?.trim(), row]).filter(([hash]) => hash)), // hashToRowMap
        allHashes
      );

      // Step 5: Assign labels to remaining unlabeled paths
      this.assignLabelsToRemainingPathsWithImmediateBranching(
        data,
        new Set(data.map((row) => row[0]?.trim()).filter(Boolean)), // usedLabels
        new Map(data.map((row) => [row[1]?.trim(), row]).filter(([hash]) => hash)), // hashToRowMap
        allHashes
      );

      // Step 6: Mark transactions with outstanding paths
      const hashToRowMap = new Map(
        data.map((row) => [row[1]?.trim(), row]).filter(([hash]) => hash)
      );
      this.markOutstandingPaths(data, hashToRowMap, allHashes);

      // Step 7: Classify asterisk-marked transactions
      this.classifyOutstandingPaths(data, hashToRowMap, allHashes);

      // Step 8: Process linear, non-branching paths
      this.processLinearNonBranchingPaths(data, hashToRowMap, allHashes);

      // Step 9: Process eventually branching paths
      this.processEventuallyBranchingPaths(data, hashToRowMap, allHashes);

      // Step 10: Handle paths marked with '#'
      this.processImmediateBranchingPaths(data, hashToRowMap, allHashes);

      // Iteratively apply steps 6–10
      const maxIterations = 50; // Limit to prevent infinite loop
      let iterations = 0;

      while (iterations < maxIterations) {
        console.log(`Iteration ${iterations + 1}: Processing outstanding paths...`);

        // Step 6: Mark transactions with outstanding paths
        this.markOutstandingPaths(data, hashToRowMap, allHashes);

        // Step 7: Classify asterisk-marked transactions
        this.classifyOutstandingPaths(data, hashToRowMap, allHashes);

        // Step 8: Process linear, non-branching paths
        this.processLinearNonBranchingPaths(data, hashToRowMap, allHashes);

        // Step 9: Process eventually branching paths
        this.processEventuallyBranchingPaths(data, hashToRowMap, allHashes);

        // Step 10: Handle paths marked with '#'
        this.processImmediateBranchingPaths(data, hashToRowMap, allHashes);

        const unlabeledRows = data.filter((row) => {
          // A row is considered "empty" if all its cells are empty
          const isEmptyRow = row.every((cell) => !cell?.trim());
          // A row is "unlabeled" if it's not empty and Column A (index 0) is empty
          const isUnlabeled = !row[0]?.trim();

          return !isEmptyRow && isUnlabeled;
        });

        if (unlabeledRows.length === 0) {
          console.log("All transactions labeled. Exiting loop.");
          break;
        }

        iterations++;
      }

      if (iterations >= maxIterations) {
        console.warn(
          `Reached maximum iterations (${maxIterations}). Some paths may remain unlabeled.`
        );
      }

      if (this.chain === "BTC") {
        this.copyLabelsByTransactionHash(data, originalData);
        return originalData;
      }

      return data;
    },

    promoteBitcoinEntryContinuationDataForLabeling(data) {
      const flushEntry = (entryRows) => {
        if (!entryRows || entryRows.length === 0) return;

        const firstRow = entryRows[0];
        const childHashes = [];

        entryRows.forEach((entryRow) => {
          const continuesAs = this.getCell(entryRow, 16);
          this.extractContinuesAsHashes(continuesAs).forEach((hash) => {
            if (!childHashes.includes(hash)) childHashes.push(hash);
          });
        });

        if (childHashes.length > 0) {
          firstRow[16] = `Continues as ${childHashes.join(", ")}`;
        }
      };

      let currentEntry = [];

      data.forEach((row) => {
        const transactionHash = this.getCell(row, 1);
        const isEmptyRow = this.isRowEmpty(row);

        if (transactionHash || isEmptyRow) {
          flushEntry(currentEntry);
          currentEntry = transactionHash ? [row] : [];
          return;
        }

        if (currentEntry.length > 0) {
          currentEntry.push(row);
        }
      });

      flushEntry(currentEntry);
    },

    copyLabelsByTransactionHash(sourceData, targetData) {
      const labelsByHash = new Map();

      sourceData.forEach((row) => {
        const transactionHash = this.getCell(row, 1);
        if (transactionHash) labelsByHash.set(transactionHash, this.getCell(row, 0));
      });

      targetData.forEach((row) => {
        const transactionHash = this.getCell(row, 1);
        if (transactionHash && labelsByHash.has(transactionHash)) {
          row[0] = labelsByHash.get(transactionHash);
        }
      });
    },

    runTool50_All(inputData) {
      const { entries, missingLabels } = this.extractEntries(inputData);

      if (missingLabels.length > 0) {
        return { data: [], missingLabels };
      }

      const sortedEntries = this.sortEntries(entries);
      const emptyRowWidth = Math.max(
        ...inputData.map((row) => (Array.isArray(row) ? row.length : 0)),
        18
      );
      const makeEmptyRow = () => Array(emptyRowWidth).fill("");

      const useExtraSpacing =
        this.chain === "BTC" && this.extraSpacing === true;

      // Flatten entries (Tool 50 vs Tool 50B behavior)
      const flattenedData = sortedEntries.flatMap((entry) => {
        if (useExtraSpacing) {
          // Tool 50B: Always add an empty row after each entry
          return [...entry, makeEmptyRow()];
        }

        // Tool 50: Only add an empty row if last row note includes "path ends"
        const lastRow = entry[entry.length - 1];
        const note = lastRow[9]?.toLowerCase() || ""; // Column J
        const shouldAddEmptyRow = note.includes("path ends");
        return [...entry, ...(shouldAddEmptyRow ? [makeEmptyRow()] : [])];
      });

      // Remove any trailing empty rows after the last entry (same as Tool 50 / 50B)
      if (flattenedData.length > 0) {
        while (flattenedData[flattenedData.length - 1].every((cell) => !String(cell || "").trim())) {
          flattenedData.pop();
        }
      }

      return { data: flattenedData, missingLabels: [] };
    },

    removeDuplicatesWithNotes(data) {
      const seenTransactionIds = new Map();
      const branchPaths = new Map(); // Track branches per From Hash
      const resultData = [];
      const headers = data[0];
      resultData.push(headers);

      // First pass: Identify branches and duplicates
      data.slice(1).forEach((row, rowIndex) => {
        const transactionId = row[1]?.trim(); // Column B
        const fundsFollowed = row[12]; // Column M
        const balanceData = row[13]; // Column N
        const fromHash = row[14]; // Column O
        const continuesAs = row[16]?.trim(); // Column Q

        // Preserve empty rows as they are
        if (row.every(cell => cell === "")) {
          resultData.push(row);
          return;
        }

        if (!transactionId) {
          console.log(`Skipping row ${rowIndex + 1} with missing Transaction ID.`);
          return;
        }

        // Track branching
        if (fromHash) {
          if (!branchPaths.has(fromHash)) {
            branchPaths.set(fromHash, []);
          }
          branchPaths.get(fromHash).push(transactionId); // Map From Hash to its branches
        }

        // Handle duplicates
        if (!seenTransactionIds.has(transactionId)) {
          seenTransactionIds.set(transactionId, {
            row: [...row],
            aggregatedNotes: [], // For Column R
            branchNotes: [],     // For Column J
            totalFunds: parseFloat(fundsFollowed?.split(' ')[0]?.replace(/,/g, '') || 0),
            tokenType: fundsFollowed?.split(' ')[1] || null,
            originalDetails: [],
            continuesAsDetails: continuesAs ? [`Continues as: ${continuesAs}`] : [], // Add "Continues as" details if present
          });

          // Add original row details
          if (fundsFollowed) seenTransactionIds.get(transactionId).originalDetails.push(`Funds Followed: ${fundsFollowed}`);
          if (balanceData) seenTransactionIds.get(transactionId).originalDetails.push(`Balance: ${balanceData}`);
          if (fromHash) seenTransactionIds.get(transactionId).originalDetails.push(`From: ${fromHash}`);
          console.log(`First occurrence for Transaction ID: ${transactionId} at row ${rowIndex + 1}`);
        } else {

          this.duplicateCount++; // Increment on each duplicate
          // Duplicate found, aggregate data
          const record = seenTransactionIds.get(transactionId);

          if (fundsFollowed) {
            const [amount, tokenType] = fundsFollowed.split(' ');
            const numericAmount = parseFloat(amount.replace(/,/g, '')) || 0;

            record.totalFunds += numericAmount;
            if (!record.tokenType) record.tokenType = tokenType; // Assume the first token type is correct
          }

          const noteDetails = [];
          if (fundsFollowed) noteDetails.push(`Funds Followed: ${fundsFollowed}`);
          if (balanceData) noteDetails.push(`Balance: ${balanceData}`);
          if (fromHash) noteDetails.push(`From: ${fromHash}`);
          record.aggregatedNotes.push(noteDetails.join(', '));

          // Append "Continues as" details if present
          if (continuesAs) {
            record.continuesAsDetails.push(`Continues as: ${continuesAs}`);
          }
        }
      });

      // Second pass: Add branching notes and update Column M
      branchPaths.forEach((branches, fromHash) => {
        // Filter out duplicate hashes in the branch list
        const uniqueBranches = [...new Set(branches)];

        // Only consider it a branch if there are multiple unique transaction hashes
        if (uniqueBranches.length > 1) {
          seenTransactionIds.forEach(({ row, branchNotes }, transactionId) => {
            console.log(transactionId) // this is required to pass eslint check... or it is defined but never used
            if (row[1]?.trim() === fromHash) {
              const branchNote = `Branches as ${uniqueBranches.join(', ')}`;
              branchNotes.push(branchNote); // Append branch note for Column J
              console.log(`Branch Note for Transaction ID ${fromHash}:`, branchNote);
            }
          });
        } else {
          console.log(`Ignoring branches for ${fromHash} as there is only one unique hash.`);
        }
      });

      // Generate results for duplicates and branching
      seenTransactionIds.forEach(({ row, aggregatedNotes, branchNotes, totalFunds, tokenType, originalDetails, continuesAsDetails }) => {
        // Update Column R with duplicate-related notes
        if (aggregatedNotes.length > 0 || continuesAsDetails.length > 0) {
          const allDetails = [
            `(Details 1: ${originalDetails.join(', ')})`,
            ...aggregatedNotes.map((details, index) => `(Details ${index + 2}: ${details})`),
            ...continuesAsDetails, // Include "Continues as" details
          ];
          const noteString = `This transaction follows ${totalFunds.toLocaleString()} ${tokenType}. Details: ${allDetails.join(' ')}`;
          row[17] = row[17] ? `${row[17]}\n${noteString}` : noteString; // Update Column R

          // Update Column M with the total funds and token type
          row[12] = `${totalFunds.toLocaleString()} ${tokenType}`; // Column M
          console.log(`Updated Column M for Transaction ID ${row[1]}:`, row[12]);
        }

        // Update Column J with branching notes
        if (branchNotes.length > 0) {
          const branchNoteString = branchNotes.join('\n');
          row[9] = row[9] ? `${row[9]}\n${branchNoteString}` : branchNoteString; // Update Column J
          console.log(`Branch Note for Transaction ID ${row[1]}:`, branchNoteString);
        }

        // Push the updated row to results
        resultData.push(row);

        // Add an empty row after rows with specific notes in Column J
        const branchColumn = row[9]?.toLowerCase() || ''; // Ensure case-insensitive comparison
        if (branchColumn.includes('path ends') || branchColumn.includes('funds remain')) {
          resultData.push(Array(row.length).fill('')); // Add an empty row
          console.log(`Added empty row after Transaction ID ${row[1]} due to note in Column J.`);
        }
      });

      // Ensure only rows with data are included, preserving the positions of original data
      return resultData.filter(row => row.some(cell => cell !== "" || row === headers));
    },

    addEmptyRowsAfterNotes(data) {
      const updatedData = [];
      data.forEach(row => {
        updatedData.push(row);
        const branchColumn = row[9]?.toLowerCase() || ''; // Column J
        // Check if the note contains "Path ends" or "Funds remain"
        if (branchColumn.includes('path ends') || branchColumn.includes('funds remain')) {
          updatedData.push(Array(row.length).fill('')); // Add an empty row
          console.log(`Added empty row after note in Column J: "${row[9]}"`);
        }
      });
      return updatedData;
    },

    removeBitcoinDuplicates(data) {
      const seenHashes = new Map();
      const resultData = [];
      let currentTransaction = [];
      let currentTransactionHash = "";

      data.forEach((row) => {
        // More robust handling of potentially null or undefined cells in Column B
        const transactionHash = row[1] ? row[1].trim() : "";
        // Updated to handle potential null or undefined values in any cell
     const isEmptyRow = row.every(cell => !(cell && String(cell).trim()));

        if (transactionHash) {
          if (transactionHash !== currentTransactionHash) {
            if (seenHashes.has(currentTransactionHash)) {
              // If current transaction hash has been seen, merge data
              const baseTransaction = seenHashes.get(currentTransactionHash);
              const appendedRows = this.mergeTransactionData(baseTransaction, currentTransaction);
              this.insertMergedRowsIntoResultData(resultData, baseTransaction, appendedRows);
            } else if (currentTransactionHash) {
              // If it's a new hash and not seen before, push the current transaction
              seenHashes.set(currentTransactionHash, currentTransaction);
              resultData.push(...currentTransaction);
            }
            // Reset current transaction with the new hash row
            currentTransaction = [row];
            currentTransactionHash = transactionHash;
          } else {
            // Continue adding to the current transaction block
            currentTransaction.push(row);
          }
        } else if (!isEmptyRow) {
          // Add to current transaction if the row is not empty
          currentTransaction.push(row);
        }

        if (isEmptyRow && currentTransactionHash) {
          // Handle end of a transaction block
          if (seenHashes.has(currentTransactionHash)) {
            const baseTransaction = seenHashes.get(currentTransactionHash);
            const appendedRows = this.mergeTransactionData(baseTransaction, currentTransaction);
            this.insertMergedRowsIntoResultData(resultData, baseTransaction, appendedRows);
          } else {
            seenHashes.set(currentTransactionHash, currentTransaction);
            resultData.push(...currentTransaction);
          }
          // Reset for the next transaction
          currentTransaction = [];
          currentTransactionHash = "";
          resultData.push(row); // Push the empty row as a separator
        }
      });

      // Check last transaction block
      if (currentTransactionHash && seenHashes.has(currentTransactionHash)) {
        const baseTransaction = seenHashes.get(currentTransactionHash);
        const appendedRows = this.mergeTransactionData(baseTransaction, currentTransaction);
        this.insertMergedRowsIntoResultData(resultData, baseTransaction, appendedRows);
      } else if (currentTransactionHash) {
        seenHashes.set(currentTransactionHash, currentTransaction);
        resultData.push(...currentTransaction);
      }

      this.duplicateCount = seenHashes.size; // Update the count of unique transactions
      return resultData;
    },

    mergeTransactionData(baseTransaction, newTransaction) {
        const appendedRows = [];

        newTransaction.forEach((newRow, index) => {
            const cleanedNewRow = newRow.map(cell => (cell ? String(cell).trim() : ""));
            const targetIndex = this.findBitcoinMergeTargetRowIndex(baseTransaction, cleanedNewRow, index);

            if (targetIndex >= 0) {
                this.mergeBitcoinRowData(baseTransaction[targetIndex], cleanedNewRow);
            } else {
                // Add new BTC output rows that are present in this duplicate transaction story but
                // not yet represented in the base entry. Clear A/B/C so this remains an additional
                // row inside the same transaction entry instead of becoming a separate entry.
                const preparedRow = this.prepareAdditionalBitcoinOutputRow(cleanedNewRow);
                baseTransaction.push(preparedRow);
                appendedRows.push(preparedRow);
            }
        });

        return appendedRows;
    },

    insertMergedRowsIntoResultData(resultData, baseTransaction, appendedRows) {
        if (!appendedRows || appendedRows.length === 0) return;

        const appendedSet = new Set(appendedRows);
        const existingBaseRows = new Set(baseTransaction.filter(row => !appendedSet.has(row)));
        let insertAfterIndex = -1;

        resultData.forEach((row, index) => {
            if (existingBaseRows.has(row)) insertAfterIndex = index;
        });

        if (insertAfterIndex === -1) {
            resultData.push(...appendedRows);
            return;
        }

        resultData.splice(insertAfterIndex + 1, 0, ...appendedRows);
    },

    findBitcoinMergeTargetRowIndex(baseTransaction, newRow, fallbackIndex) {
        const outputIdentity = this.getBitcoinOutputIdentity(newRow);
        const carriesOutputStory = this.rowHasBitcoinOutputStory(newRow);

        // BTC rows with Column D plus M/Q/R are output-specific stories. Match them by the
        // output itself instead of by row number, otherwise a second same-hash entry can collapse
        // a different output into the first output row and lose its Q/R data.
        if (outputIdentity && carriesOutputStory) {
            const exactOutputIndex = baseTransaction.findIndex((baseRow) =>
                this.getBitcoinOutputIdentity(baseRow) === outputIdentity
            );

            if (exactOutputIndex !== -1) return exactOutputIndex;
            return -1;
        }

        // Keep the older row-index behavior for ordinary note/input rows and rows without a
        // distinct followed-output story.
        return fallbackIndex < baseTransaction.length ? fallbackIndex : -1;
    },

    getBitcoinOutputIdentity(row) {
        const toAddress = this.getCell(row, 3);
        if (!toAddress) return "";

        // Amount + token distinguish multiple outputs to the same address when the export includes them.
        return [toAddress, this.getCell(row, 5).replace(/,/g, ""), this.getCell(row, 6)].join("||");
    },

    rowHasBitcoinOutputStory(row) {
        return !!(
            this.getCell(row, 3) &&
            (this.getCell(row, 12) || this.getCell(row, 16) || this.getCell(row, 17))
        );
    },

    prepareAdditionalBitcoinOutputRow(row) {
        const preparedRow = row.map(cell => (cell ? String(cell).trim() : ""));

        while (preparedRow.length < 18) preparedRow.push("");

        // Additional rows belong to the current entry. Do not let a duplicate label/hash/from-address
        // turn this output story into a separate transaction entry after the merge.
        preparedRow[0] = "";
        preparedRow[1] = "";
        preparedRow[2] = "";
        preparedRow[14] = ""; // Column O: avoid creating a blank child in Tool 49B.
        return preparedRow;
    },

    mergeBitcoinRowData(baseRow, newRow) {
        const maxLength = Math.max(baseRow.length, newRow.length, 18);
        for (let i = 0; i < maxLength; i++) {
            if (baseRow[i] === undefined) baseRow[i] = "";
        }

        // Preserve identifying output cells when the base row is sparse.
        [3, 4, 5, 6].forEach((i) => {
            const newValue = this.getCell(newRow, i);
            if (newValue && !this.getCell(baseRow, i)) {
                baseRow[i] = newValue;
            }
        });

        for (let i = 9; i <= 17; i++) { // Columns J to R; BTC output rows need M, Q, and R preserved together.
            // Safely handle potential null values in newRow and baseTransaction
            const newValue = this.getCell(newRow, i);
            if (newValue) {
                if (i === 12) { // Column M (Funds Followed)
                    const baseValue = this.getCell(baseRow, i);
                    baseRow[i] = baseValue ? this.sumBTC(baseValue, newValue) : newValue;
                } else if (i === 16) { // Column Q (Continues as)
                    const baseValue = this.getCell(baseRow, i);
                    baseRow[i] = this.mergeContinuesAsCell(baseValue, newValue);
                } else if (i === 17) { // Column R (Left bounds / system data)
                    const baseValue = this.getCell(baseRow, i);
                    baseRow[i] = this.mergeMultilineCell(baseValue, newValue);
                } else {
                    baseRow[i] = newValue;
                }
            }
        }
    },

    getCell(row, index) {
        return row && row[index] !== undefined && row[index] !== null
            ? String(row[index]).trim()
            : "";
    },

    mergeContinuesAsCell(baseValue, newValue) {
        if (!baseValue) return newValue;
        if (!newValue || newValue === baseValue) return baseValue;

        const baseHashes = this.extractContinuesAsHashes(baseValue);
        const newHashes = this.extractContinuesAsHashes(newValue);

        // If either side says the output continues, preserve the child hash list rather than letting
        // a duplicate "Path ends." value erase the more specific continuation story.
        if (baseHashes.length > 0 || newHashes.length > 0) {
            const mergedHashes = [...new Set([...baseHashes, ...newHashes])];
            return mergedHashes.length > 0 ? `Continues as ${mergedHashes.join(", ")}` : "Path ends.";
        }

        return this.mergeMultilineCell(baseValue, newValue);
    },

    extractContinuesAsHashes(value) {
        const text = String(value || "").trim();
        if (!text.startsWith("Continues as")) return [];

        return text
            .replace(/^Continues as\s*/, "")
            .split(",")
            .map((hash) => hash.trim())
            .filter(Boolean);
    },

    mergeMultilineCell(baseValue, newValue) {
        if (!baseValue) return newValue;
        if (!newValue || newValue === baseValue) return baseValue;

        const existingLines = baseValue
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);

        const newLines = newValue
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);

        const combinedLines = [...existingLines];
        newLines.forEach((line) => {
            if (!combinedLines.includes(line)) combinedLines.push(line);
        });

        return combinedLines.join("\n");
    },

    sumBTC(btcString1, btcString2) {
        const parseBTCAmount = (btcString) => {
            const amountText = String(btcString || "0").trim().split(/\s+/)[0] || "0";
            return parseFloat(amountText.replace(/,/g, "")) || 0;
        };

        const tokenText = String(btcString2 || btcString1 || "BTC").trim().split(/\s+/)[1] || "BTC";
        const amount1 = parseBTCAmount(btcString1);
        const amount2 = parseBTCAmount(btcString2);
        const summedAmount = (amount1 + amount2).toFixed(8); // Fixed to 8 decimal places for BTC
        return `${summedAmount} ${tokenText}`;
    },

    cleanupEmptyRows(data) {
      const cleanedData = [];
      let lastRowEmpty = false;

      data.forEach((row) => {
        // Safely check if the row is empty
        const isEmptyRow = row.every((cell) => !String(cell || "").trim());

        if (isEmptyRow) {
          if (!lastRowEmpty) {
            cleanedData.push(row); // Add only one empty row
          }
          lastRowEmpty = true;
        } else {
          cleanedData.push(row);
          lastRowEmpty = false;
        }
      });

      return cleanedData;
    },

    fixContinuationData(data) {
      const transactionMap = new Map();

      // Step 1: Create a map of transactions based on column B
      data.forEach((row, index) => {
        const transactionHash = this.getCell(row, 1);
        if (transactionHash && !transactionMap.has(transactionHash)) {
          transactionMap.set(transactionHash, index);
        }
      });

      // Step 2: Process each transaction and update column Q as needed
      data.forEach((row, rowIndex) => {
        const fromHash = row[14]?.trim(); // Column O ("From")
        const currentHash = row[1]?.trim(); // Column B
        if (fromHash && currentHash && transactionMap.has(fromHash)) {
          const fromRowIndex = transactionMap.get(fromHash);

          if (this.chain === "BTC") {
            const bitcoinTargetRows = this.findBitcoinContinuationSourceRows(data, fromRowIndex, rowIndex);

            if (bitcoinTargetRows.length > 0) {
              const sourceHasOutputSpecificStory = this.bitcoinEntryHasOutputSpecificStory(data, fromRowIndex);

              bitcoinTargetRows.forEach((targetRow) => {
                // In newer WayFinder BTC exports, an explicit output-row "Path ends." is
                // authoritative. Do not change that row to a continuation just because
                // the same address also appears as an input in a later BTC transaction.
                if (
                  sourceHasOutputSpecificStory &&
                  this.getCell(targetRow, 16) === "Path ends." &&
                  this.rowHasBitcoinOutputStory(targetRow)
                ) {
                  return;
                }

                this.applyContinuationToRow(targetRow, currentHash, fromHash);
              });
              return;
            }

            // If this BTC source entry already has output-specific story rows, do not fall back
            // to the first row. That first row may describe a different output that really ends.
            if (this.bitcoinEntryHasOutputSpecificStory(data, fromRowIndex)) {
              return;
            }
          }

          const fromRow = data[fromRowIndex];
          this.applyContinuationToRow(fromRow, currentHash, fromHash);
        }
      });

      // Step 3: Deduplicate "Continues as" entries and ensure no self-references.
      // BTC continuation rows can have an empty Column B while still belonging to the
      // current transaction entry, so track the active entry hash instead of relying
      // only on the row's own Column B.
      let activeEntryHash = "";
      data.forEach((row) => {
        const rowTransactionHash = this.getCell(row, 1); // Column B
        const isEmptyRow = this.isRowEmpty(row);

        if (rowTransactionHash) {
          activeEntryHash = rowTransactionHash;
        } else if (isEmptyRow) {
          activeEntryHash = "";
        }

        const transactionHash = rowTransactionHash || (this.chain === "BTC" ? activeEntryHash : "");
        const continuesAsField = this.getCell(row, 16); // Column Q ("Continues as")

        if (continuesAsField.startsWith("Continues as")) {
          const hashes = continuesAsField
            .replace("Continues as", "")
            .split(",")
            .map((hash) => hash.trim())
            .filter((hash) => hash && hash !== transactionHash); // Remove blanks and self-references

          if (hashes.length === 0) {
            // If no hashes remain, set to "Path ends."
            row[16] = "Path ends.";
            this.changesLog.push(
              `Changed "Continues as" to "Path ends." for transaction ${transactionHash} because no valid hashes remained.`
            );
          } else {
            const uniqueHashes = [...new Set(hashes)]; // Remove duplicates
            row[16] = `Continues as ${uniqueHashes.join(", ")}`; // Rebuild the field
            if (hashes.length !== uniqueHashes.length) {
              this.changesLog.push(
                `Deduplicated "Continues as" field for transaction ${transactionHash}.`
              );
            }
          }
        }
      });

      return data;
    },

    applyContinuationToRow(row, currentHash, fromHash) {
      if (!row || !currentHash) return;

      const continuesAs = this.getCell(row, 16); // Column Q ("Continues as")
      const existingHashes = this.extractContinuesAsHashes(continuesAs);

      if (!continuesAs || continuesAs === "Path ends.") {
        row[16] = `Continues as ${currentHash}`;
        this.changesLog.push(
          `${continuesAs === "Path ends." ? "Replaced" : "Initialized"} "Continues as ${currentHash}" for transaction ${fromHash}.`
        );
        return;
      }

      if (continuesAs.startsWith("Continues as")) {
        if (!existingHashes.includes(currentHash)) {
          row[16] = `Continues as ${[...existingHashes, currentHash].join(", ")}`;
          this.changesLog.push(
            `Appended "${currentHash}" to "Continues as" for transaction ${fromHash}.`
          );
        }
        return;
      }

      row[16] = this.mergeContinuesAsCell(continuesAs, `Continues as ${currentHash}`);
    },

    findBitcoinContinuationSourceRows(data, sourceStartIndex, childStartIndex) {
      const sourceRows = this.getEntryRowsFromStartIndex(data, sourceStartIndex);
      const childRows = this.getEntryRowsFromStartIndex(data, childStartIndex);
      const childInputAddresses = new Set(
        childRows
          .map((childRow) => this.getCell(childRow, 2)) // Column C: BTC input/from address
          .filter(Boolean)
      );

      if (childInputAddresses.size === 0) return [];

      return sourceRows.filter((sourceRow) => {
        const outputAddress = this.getCell(sourceRow, 3); // Column D: BTC output/to address
        return outputAddress && childInputAddresses.has(outputAddress);
      });
    },

    bitcoinEntryHasOutputSpecificStory(data, startIndex) {
      return this.getEntryRowsFromStartIndex(data, startIndex).some((row) =>
        this.rowHasBitcoinOutputStory(row)
      );
    },

    getEntryRowsFromStartIndex(data, startIndex) {
      const rows = [];

      if (startIndex < 0 || startIndex >= data.length) return rows;

      for (let i = startIndex; i < data.length; i++) {
        const row = data[i];
        const transactionHash = this.getCell(row, 1);
        const isEmptyRow = this.isRowEmpty(row);

        if (i > startIndex && (transactionHash || isEmptyRow)) break;
        if (isEmptyRow) break;

        rows.push(row);
      }

      return rows;
    },

    isRowEmpty(row) {
      return !row || row.every((cell) => !String(cell || "").trim());
    },

    labelOriginWithBaseLabel(data, originHash) {
        console.log("Labeling origin with base label...");

        // Locate the row for the origin hash
        const originRow = data.find(row => row[1]?.trim() === originHash);
        if (!originRow) {
          console.error("Origin hash not found in the data. Cannot label origin.");
          return;
        }

        // Assign the base label to the origin
        originRow[0] = this.baseLabel;
        console.log(`Assigned base label "${this.baseLabel}" to origin hash "${originHash}".`);
      },

    processImmediateBranchingPaths(data, hashToRowMap, allHashes) {
     // console.log("Processing paths marked with '#' for immediate branching...");

      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows without labels or hashes, or not marked with '#'
        if (!currentLabel || !currentHash || !currentLabel.endsWith('#')) return;

        // Remove the '#' to get the base label
        const baseLabel = currentLabel.slice(0, -1);
       // console.log(`Processing path for base label: ${baseLabel}`);

        // Update the label for the original transaction to remove '#'
        row[0] = baseLabel;

        // Check the "Continues as" field for child hashes
        const continuesAs = row[16]?.trim(); // Column Q ("Continues as")
        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        if (childHashes.length === 0) {
       //   console.log(`No child hashes found for hash ${currentHash}.`);
          return;
        }

        console.log(`Found ${childHashes.length} branches for hash ${currentHash}.`);

        // Determine if the base label ends with a number or letter
        const isLastNumeric = !isNaN(baseLabel.slice(-1));
        let index = 1; // Start numbering for branches

        childHashes.forEach(childHash => {
          const childRow = hashToRowMap.get(childHash);

          if (!childRow) {
         //   console.warn(`Child hash ${childHash} not found in data. Skipping.`);
            return;
          }

          // Skip if the child already has a label
          if (childRow[0]) {
       //     console.log(`Child hash ${childHash} already labeled as "${childRow[0]}". Skipping.`);
            return;
          }

          // Generate the new label based on alternating character logic
          let newLabel;
          if (isLastNumeric) {
            newLabel = `${baseLabel}${String.fromCharCode(96 + index)}`; // Append letters (a, b, c...)
          } else {
            newLabel = `${baseLabel}${index}`; // Append numbers (1, 2, 3...)
          }

          // Assign the new label
          childRow[0] = newLabel;
        //  console.log(`Assigned label "${newLabel}" to child hash ${childHash}.`);

          // Increment index for next branch
          index++;
        });
      });

    //  console.log("Completed processing paths marked with '#'.");
    },

    markOutstandingPaths(data, hashToRowMap, allHashes) {
      console.log("Marking transactions with outstanding paths...");

      // Iterate over all rows to identify transactions with incomplete labeling
      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows without labels or hashes
        if (!currentLabel || !currentHash) return;

        console.log(`Checking transaction "${currentLabel}" (Hash: ${currentHash}) for outstanding paths.`);

        // Check the "Continues as" field for child hashes
        const continuesAs = row[16]?.trim(); // Column Q ("Continues as")
        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        // If at least one child hash exists but is not labeled, mark this transaction
        const hasOutstandingPaths = childHashes.some(childHash => {
          const childRow = hashToRowMap.get(childHash);
          return !childRow || !childRow[0]; // Check if the child has no label
        });

        if (hasOutstandingPaths) {
          // Mark this row with an asterisk to indicate outstanding paths
          row[0] = `${currentLabel}*`;
          console.log(`Marked transaction "${currentLabel}" as having outstanding paths.`);
        }
      });

      console.log("Completed marking transactions with outstanding paths.");
    },

    processEventuallyBranchingPaths(data, hashToRowMap, allHashes) {
      console.log("Processing paths marked with '~' for eventual branching...");

      // Iterate through all rows to find paths marked with '~'
      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows without labels or hashes, or not marked with '~'
        if (!currentLabel || !currentHash || !currentLabel.endsWith('~')) return;

        // Remove the '~' to get the base label
        const baseLabel = currentLabel.slice(0, -1);
        console.log(`Processing path for base label: ${baseLabel}`);

        // Update the label for the original transaction to remove '~'
        row[0] = baseLabel;
        console.log(`Cleaned up '~' symbol from label for transaction: ${currentHash}`);

        let currentPathHash = currentHash;
        let isLastNumeric = !isNaN(baseLabel.slice(-1)); // Check if the last character is numeric
        let index = 1; // Start with 1 for numbers or 'a' for letters

        while (currentPathHash) {
          const currentRow = hashToRowMap.get(currentPathHash);

          if (!currentRow) {
            console.warn(`Row for hash ${currentPathHash} not found. Terminating path.`);
            break;
          }

          const continuesAs = currentRow[16]?.trim(); // Column Q ("Continues as")
          if (!continuesAs || continuesAs === "Path ends.") {
            console.log(`End of path detected for hash ${currentPathHash}.`);
            break;
          }

          const childHashes = this.extractChildHashes(continuesAs, allHashes);

          // Stop processing if more than one child (branching detected)
          if (childHashes.length > 1) {
            console.log(`Branching detected at hash ${currentPathHash}. Stopping further labeling.`);
            break;
          }

          // Process the next hash in the path
          const nextHash = childHashes[0];
          const nextRow = hashToRowMap.get(nextHash);

          if (!nextRow) {
            console.warn(`Child hash ${nextHash} not found. Terminating path.`);
            break;
          }

          // Skip if the next row already has a label
          if (nextRow[0]) {
            console.log(`Hash ${nextHash} already labeled as ${nextRow[0]}. Skipping.`);
            break;
          }

          // Generate the next label based on alternating character logic
          let newLabel;
          if (isLastNumeric) {
            const letter = String.fromCharCode(96 + index); // 'a', 'b', 'c', ...
            newLabel = `${baseLabel}${letter}`;
          } else {
            newLabel = `${baseLabel}${index}`;
          }

          // Assign the label and update the base label
          nextRow[0] = newLabel;
          console.log(`Assigned label "${newLabel}" to hash ${nextHash}`);

          // Update for the next iteration
          currentPathHash = nextHash;
          index++;
        }
      });

      console.log("Completed processing paths marked with '~'.");
    },

    classifyOutstandingPaths(data, hashToRowMap, allHashes) {
     // console.log("Classifying transactions marked with an asterisk...");

      // Iterate over rows with labels ending in '*'
      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows that are not marked with an asterisk
        if (!currentLabel?.endsWith('*') || !currentHash) return;

        //console.log(`Checking asterisk-marked transaction "${currentLabel}" (Hash: ${currentHash}) for classification.`);

        // Remove the asterisk for clean reclassification
        row[0] = currentLabel.slice(0, -1);

        // Check the "Continues as" field for child hashes
        const continuesAs = row[16]?.trim(); // Column Q ("Continues as")
        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        if (childHashes.length > 1) {
          // Immediate branching: Mark with `#`
          row[0] = `${row[0]}#`;
        //  console.log(`Marked transaction "${currentLabel}" as branching immediately (#).`);
        } else if (childHashes.length === 1) {
          // Check the downstream path to determine if it eventually branches
          const downstreamBranching = this.doesPathEventuallyBranchInFinalStep(childHashes[0], hashToRowMap, allHashes);

          if (downstreamBranching) {
            row[0] = `${row[0]}~`; // Indicates a downstream branch
         //  console.log(`Marked transaction "${currentLabel}" as linear with downstream branching (~).`);
          } else {
            row[0] = `${row[0]}>`; // Indicates a fully linear path
         //   console.log(`Marked transaction "${currentLabel}" as fully linear (>).`);
          }
        } else {
          // No continuation or no labeled children: Retain `*`
          row[0] = `${row[0]}*`;
       //   console.log(`Transaction "${currentLabel}" has no valid paths and remains as (*).`);
        }
      });

      //console.log("Completed classifying asterisk-marked transactions.");
    },

    processLinearNonBranchingPaths(data, hashToRowMap, allHashes) {
     // console.log("Processing linear and non-branching paths (marked with '>')...");

      // Iterate through all rows to find paths marked with '>'
      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows without labels or hashes, or not marked with '>'
        if (!currentLabel || !currentHash || !currentLabel.endsWith('>')) return;

        // Remove the '>' to get the base label
        const baseLabel = currentLabel.slice(0, -1);
      //  console.log(`Processing linear path for base label: ${baseLabel}`);

        // Update the label for the original transaction to remove '>'
        row[0] = baseLabel;
       // console.log(`Cleaned up '>' symbol from label for transaction: ${currentHash}`);

        let currentIndex = 1; // Decimal counter for the label
        let currentPathHash = currentHash;

        while (currentPathHash) {
          const currentRow = hashToRowMap.get(currentPathHash);

          if (!currentRow) {
            console.warn(`Row for hash ${currentPathHash} not found. Terminating path.`);
            break;
          }

          const continuesAs = currentRow[16]?.trim(); // Column Q ("Continues as")
          if (!continuesAs || continuesAs === "Path ends.") {
           // console.log(`End of path detected for hash ${currentPathHash}.`);
            break;
          }

          const childHashes = this.extractChildHashes(continuesAs, allHashes);

          // Stop if more than one child hash (branching occurs)
          if (childHashes.length > 1) {
         //   console.log(`Branching detected at hash ${currentPathHash}. Stopping processing for this path.`);
            break;
          }

          // Assign decimal label to the next transaction in the path
          const nextHash = childHashes[0];
          const nextRow = hashToRowMap.get(nextHash);

          if (!nextRow) {
          //  console.warn(`Child hash ${nextHash} not found in data. Terminating path.`);
            break;
          }

          // Skip if the next row already has a label
          if (nextRow[0]) {
          //  console.log(`Hash ${nextHash} already labeled as ${nextRow[0]}. Skipping.`);
            break;
          }

          // Assign the decimal-based label
          const newLabel = `${baseLabel}.${currentIndex}`;
          nextRow[0] = newLabel;
         // console.log(`Assigned label "${newLabel}" to hash ${nextHash}.`);

          // Update for the next iteration
          currentPathHash = nextHash;
          currentIndex++;
        }
      });

     //// console.log("Completed processing linear and non-branching paths.");
    },

    assignLabelsToRemainingPathsWithImmediateBranching(data, usedLabels, hashToRowMap, allHashes) {
     // console.log("Assigning labels to remaining paths where branching continues...");

      data.forEach(row => {
        const currentLabel = row[0]?.trim(); // Path label (Column A)
        const currentHash = row[1]?.trim(); // Transaction hash (Column B)

        // Skip rows without labels or hashes
        if (!currentLabel || !currentHash) return;

        // Find child transactions from "Continues as" field
        const continuesAs = row[16]?.trim(); // Column Q ("Continues as")
        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        // Process only rows where branching occurs (more than 1 valid child)
        if (childHashes.length <= 1) return;

        console.log(`Processing labeled transaction "${currentLabel}" (Hash: ${currentHash}) with ${childHashes.length} branches.`);

        // Determine if the label ends with a letter or number
        const isLastNumeric = !isNaN(currentLabel.slice(-1));
        let index = 1; // Start with 1 for numbers or 'a' for letters

        // Assign labels to each child branch
        childHashes.forEach(childHash => {
          const childRow = hashToRowMap.get(childHash);

          if (!childRow) {
            console.warn(`Child hash "${childHash}" not found in data. Skipping.`);
            return;
          }

          // Skip if the child already has a label
          if (childRow[0]) {
            console.log(`Child hash "${childHash}" already labeled as "${childRow[0]}". Skipping.`);
            return;
          }

          // Generate the new label
          let newLabel;
          if (isLastNumeric) {
            newLabel = `${currentLabel}${String.fromCharCode(96 + index)}`; // Append letters (a, b, c...)
          } else {
            newLabel = `${currentLabel}${index}`; // Append numbers (1, 2, 3...)
          }

          // Ensure label uniqueness
          while (usedLabels.has(newLabel)) {
            index++;
            if (isLastNumeric) {
              newLabel = `${currentLabel}${String.fromCharCode(96 + index)}`;
            } else {
              newLabel = `${currentLabel}${index}`;
            }
          }

          // Assign the new label
          childRow[0] = newLabel;
          usedLabels.add(newLabel);
          console.log(`Final step - 
          Assigned label "${newLabel}" to child transaction "${childHash}".`);

          index++;
        });
      });

      console.log("Completed assigning labels to remaining paths where branching continues.");
    },

    labelRemainingPaths(data, originHash, allHashes) {
      console.log("Handling paths from the origin hash.");

      // Collect all existing labels to ensure uniqueness
      const usedLabels = new Set(data.map(row => row[0]?.trim()).filter(Boolean));
      console.log("Existing Labels:", Array.from(usedLabels));

      // Map all hashes to their respective rows for easy lookup
      const hashToRowMap = new Map();
      data.forEach(row => {
        const hash = row[1]?.trim();
        if (hash) hashToRowMap.set(hash, row);
      });

      // Get the origin row
      const originRow = hashToRowMap.get(originHash);
      if (!originRow) {
        console.error("Origin hash not found in the data. Cannot proceed.");
        return;
      }

      // Get child hashes from the origin hash
      const continuesAs = originRow[16]?.trim();
      if (!continuesAs) {
        console.log("No 'Continues as' data for the origin hash. Ending.");
        return;
      }

      const childHashes = this.extractChildHashes(continuesAs, allHashes);
      if (childHashes.length === 0) {
        console.log("No valid child hashes for the origin hash.");
        return;
      }

      // Handle immediate branching from the origin hash
      if (childHashes.length > 1) {
        console.log("Branching detected from the origin hash.");

        let branchIndex = 0; // Track branch index
        childHashes.forEach(childHash => {
          const childRow = hashToRowMap.get(childHash);

          if (!childRow) {
            console.warn(`Child hash ${childHash} not found in data. Skipping.`);
            return;
          }

          // Skip if the row already has a label
          if (childRow[0]) {
            console.log(`Hash ${childHash} already labeled as ${childRow[0]}. Skipping.`);
            return;
          }

          // Assign immediate branch labels
          const isNumeric = !isNaN(this.baseLabel.slice(-1)); // Check if base label ends with a number
          let newLabel;
          if (isNumeric) {
            newLabel = `${this.baseLabel}a${branchIndex + 1}`;
          } else {
            const letter = String.fromCharCode(97 + branchIndex); // 'a', 'b', 'c', etc.
            newLabel = `${this.baseLabel}1${letter}`;
          }

          // Ensure label uniqueness
          while (usedLabels.has(newLabel)) {
            branchIndex++;
            if (isNumeric) {
              newLabel = `${this.baseLabel}a${branchIndex + 1}`;
            } else {
              const letter = String.fromCharCode(97 + branchIndex);
              newLabel = `${this.baseLabel}1${letter}`;
            }
          }

          // Assign label to the child hash
          childRow[0] = newLabel;
          usedLabels.add(newLabel);
          console.log(`Assigned label "${newLabel}" to hash ${childHash}`);

          branchIndex++;
        });

        console.log("Completed branching labels for the origin hash.");

        // Process remaining paths for each branch
        childHashes.forEach(childHash => {
          const pathEventuallyBranches = this.doesPathEventuallyBranch(childHash, hashToRowMap, allHashes);

          if (pathEventuallyBranches) {
            // Handle paths that eventually branch
            this.labelBranchingPath(childHash, usedLabels, hashToRowMap, allHashes);
          } else {
            // Handle non-branching paths
            this.labelNonBranchingPath(childHash, usedLabels, hashToRowMap, allHashes);
          }
        });
      } else {
        console.log("No branching from the origin hash detected. Processing non-branching path.");
        const singleChildHash = childHashes[0];
        this.labelNonBranchingPath(singleChildHash, usedLabels, hashToRowMap, allHashes);
      }
    },

    doesPathEventuallyBranchInFinalStep(startHash, hashToRowMap, allHashes) {
      let currentHash = startHash;

      while (currentHash) {
        const currentRow = hashToRowMap.get(currentHash);
        if (!currentRow) {
          console.warn(`Row for hash ${currentHash} not found. Assuming no branching.`);
          return false; // Terminate if we can't find the row
        }

        const continuesAs = currentRow[16]?.trim(); // Column Q ("Continues as")
        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        if (childHashes.length > 1) {
          return true; // Branching detected
        } else if (childHashes.length === 1) {
          currentHash = childHashes[0]; // Continue following the path
        } else {
          return false; // Path ends without branching
        }
      }

      return false; // No branching detected along the path
    },

    doesPathEventuallyBranch(startHash, hashToRowMap, allHashes) {
      let currentHash = startHash;
      while (currentHash) {
        const currentRow = hashToRowMap.get(currentHash);
        if (!currentRow) return false;

        const continuesAs = currentRow[16]?.trim();
        if (!continuesAs || continuesAs === "Path ends.") return false;

        const childHashes = this.extractChildHashes(continuesAs, allHashes);
        if (childHashes.length > 1) return true;

        currentHash = childHashes[0]; // Proceed to the next hash
      }
      return false;
    },

    labelNonBranchingPath(startHash, usedLabels, hashToRowMap, allHashes) {
      console.log(`Processing non-branching path starting from: ${startHash}`);

      let currentHash = startHash; // Start with the provided branch path
      const baseLabel = hashToRowMap.get(startHash)?.[0]; // Get the current label
      if (!baseLabel) {
        console.error(`Starting hash ${startHash} has no label. Skipping.`);
        return;
      }

      let index = 1; // Incremental number for appending to baseLabel

      while (currentHash) {
        const currentRow = hashToRowMap.get(currentHash);

        if (!currentRow) {
          console.warn(`Row for hash ${currentHash} not found. Terminating path.`);
          break;
        }

        const continuesAs = currentRow[16]?.trim();
        if (!continuesAs || continuesAs === "Path ends.") {
          console.log(`End of path at hash ${currentHash}.`);
          break;
        }

        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        // If more than one child is found, stop processing (branching detected)
        if (childHashes.length > 1) {
          console.log(`Branching detected at hash ${currentHash}. Stopping sequential labeling.`);
          break;
        }

        // Process the next hash in the non-branching path
        const nextHash = childHashes[0];
        const nextRow = hashToRowMap.get(nextHash);

        if (!nextRow) {
          console.warn(`Child hash ${nextHash} not found. Terminating path.`);
          break;
        }

        // Skip if the next row already has a label
        if (nextRow[0]) {
          console.log(`Hash ${nextHash} already labeled as ${nextRow[0]}. Skipping.`);
          break;
        }

        // Assign a sequential label based on the base label
        const newLabel = `${baseLabel}.${index}`;
        nextRow[0] = newLabel;
        usedLabels.add(newLabel);
        console.log(`Assigned label "${newLabel}" to hash ${nextHash}`);

        // Update for the next iteration
        currentHash = nextHash;
        index++;
      }

      console.log(`Completed non-branching path processing for starting hash: ${startHash}`);
    },

    labelBranchingPath(startHash, usedLabels, hashToRowMap, allHashes) {
      console.log(`Processing branching path starting from: ${startHash}`);

      let currentHash = startHash;
      const baseLabel = hashToRowMap.get(startHash)?.[0]; // Get the current label
      if (!baseLabel) {
        console.error(`Starting hash ${startHash} has no label. Skipping.`);
        return;
      }

      // Determine whether the last character is numeric or alphabetic
      const isLastNumeric = !isNaN(baseLabel.slice(-1));

      let index = 0; // Start incrementing from 0 for letters or numbers
      while (currentHash) {
        const currentRow = hashToRowMap.get(currentHash);

        if (!currentRow) {
          console.warn(`Row for hash ${currentHash} not found. Terminating path.`);
          break;
        }

        const continuesAs = currentRow[16]?.trim();
        if (!continuesAs || continuesAs === "Path ends.") {
          console.log(`End of path at hash ${currentHash}.`);
          break;
        }

        const childHashes = this.extractChildHashes(continuesAs, allHashes);

        // If more than one child is found, stop processing (next branch point)
        if (childHashes.length > 1) {
          console.log(`Next branching detected at hash ${currentHash}. Stopping intermediate labeling.`);
          break;
        }

        // Process the next hash in the path
        const nextHash = childHashes[0];
        const nextRow = hashToRowMap.get(nextHash);

        if (!nextRow) {
          console.warn(`Child hash ${nextHash} not found. Terminating path.`);
          break;
        }

        // Skip if the next row already has a label
        if (nextRow[0]) {
          console.log(`Hash ${nextHash} already labeled as ${nextRow[0]}. Skipping.`);
          break;
        }

        // Generate the next label based on whether the last character is numeric or alphabetic
        let newLabel;
        if (isLastNumeric) {
          const letter = String.fromCharCode(97 + index); // 'a', 'b', 'c', ...
          newLabel = `${baseLabel}${letter}`;
        } else {
          newLabel = `${baseLabel}${index + 1}`;
        }

        // Ensure label uniqueness
        while (usedLabels.has(newLabel)) {
          index++;
          if (isLastNumeric) {
            const letter = String.fromCharCode(97 + index);
            newLabel = `${baseLabel}${letter}`;
          } else {
            newLabel = `${baseLabel}${index + 1}`;
          }
        }

        // Assign the label
        nextRow[0] = newLabel;
        usedLabels.add(newLabel);
        console.log(`Assigned label "${newLabel}" to hash ${nextHash}`);

        // Update for the next iteration
        currentHash = nextHash;
        index++;
      }

      console.log(`Completed branching path processing for starting hash: ${startHash}`);
    },

    handleSubsequentBranching(usedLabels, hashToRowMap, allHashes) {
      console.log("Handling subsequent branching...");

      // Iterate over all hashes to find branches with more than one child
      hashToRowMap.forEach((row, parentHash) => {
        const parentLabel = row[0]; // Current label
        if (!parentLabel) return; // Skip if no label assigned

        const continuesAs = row[16]?.trim();
        if (!continuesAs) return; // Skip if no 'Continues as' data

        const childHashes = this.extractChildHashes(continuesAs, allHashes);
        if (childHashes.length <= 1) return; // Skip non-branching paths

        console.log(`Processing branches from parent hash: ${parentHash} with label: ${parentLabel}`);

        // Determine if the parent label ends with a number or a letter
        const isLastNumeric = !isNaN(parentLabel.slice(-1));
        let index = 0;

        childHashes.forEach(childHash => {
          const childRow = hashToRowMap.get(childHash);
          if (!childRow) {
            console.warn(`Child hash ${childHash} not found. Skipping.`);
            return;
          }

          if (childRow[0]) {
            console.log(`Child hash ${childHash} already labeled as ${childRow[0]}. Skipping.`);
            return;
          }

          // Generate a new label for the branch
          let newLabel;
          if (isLastNumeric) {
            const letter = String.fromCharCode(97 + index); // 'a', 'b', 'c', ...
            newLabel = `${parentLabel}${letter}`;
          } else {
            newLabel = `${parentLabel}${index + 1}`;
          }

          // Ensure label uniqueness
          while (usedLabels.has(newLabel)) {
            index++;
            if (isLastNumeric) {
              const letter = String.fromCharCode(97 + index);
              newLabel = `${parentLabel}${letter}`;
            } else {
              newLabel = `${parentLabel}${index + 1}`;
            }
          }

          // Assign the label and add to usedLabels
          childRow[0] = newLabel;
          usedLabels.add(newLabel);
          console.log(`Assigned label "${newLabel}" to hash ${childHash}`);

          index++;
        });
      });

      console.log("Completed handling subsequent branching.");
    },

    collectHashes(data) {
          const allHashes = new Set();
          let originHash = null;

          data.forEach((row) => {
            if (!row || row.every((cell) => cell === "")) return; // Skip empty rows

            const hash = row[1]?.trim(); // Column B
            if (hash) {
              allHashes.add(hash);
              if (!originHash) originHash = hash; // First valid hash as origin
            }
          });

          return { allHashes, originHash };
        },

    labelLongestPathWithSimpleLabels(data, originHash, allHashes) {
          const paths = this.calculateAllPaths(data, originHash, allHashes);
          if (paths.length === 0) {
            console.log("No paths found");
            return;
          }

          // Find the longest path
          const longestPath = paths.reduce((longest, current) =>
            current.length > longest.length ? current : longest
          );

          console.log("Longest Path:", longestPath);

          // Assign labels for the longest path
          longestPath.forEach((hash, index) => {
            const rowIndex = data.findIndex((row) => row[1]?.trim() === hash);
            if (rowIndex === -1) return;

            // Assign the base label to the origin row
            if (index === 0) {
              data[rowIndex][0] = this.baseLabel;
            } else {
              // Increment subsequent rows
              const label = this.getSimpleLabel(this.baseLabel, index - 1); // Start incrementing from the second row
              data[rowIndex][0] = label;
            }
          });
        },

    calculateAllPaths(data, originHash, allHashes) {
          const hashToRowMap = new Map();

          // Map hashes to rows for easier lookup
        // data.forEach((row, index) => {
          data.forEach((row) => {
            const hash = row[1]?.trim();
            if (hash) {
              hashToRowMap.set(hash, row);
           //   console.log(`Mapped hash ${hash} to row ${index + 1}`);
            }
          });

          const paths = [];
          const traverse = (currentHash, path) => {
            console.log(`Traversing hash: ${currentHash}`);
            if (!hashToRowMap.has(currentHash)) {
              console.warn(`Hash ${currentHash} not found in the map.`);
              return;
            }

            const row = hashToRowMap.get(currentHash);
            console.log(`Processing row for hash ${currentHash}:`, row);

            const continuesAs = row[16]?.trim();
            console.log(`"Continues as" for hash ${currentHash}:`, continuesAs);

            const childHashes = this.extractChildHashes(continuesAs, allHashes);
            if (childHashes.length === 0) {
              console.log(`No valid child hashes found for hash ${currentHash}. Adding to paths.`);
              paths.push([...path, currentHash]);
              return;
            }

            childHashes.forEach((childHash) => {
              console.log(`Traversing child hash: ${childHash}`);
              traverse(childHash, [...path, currentHash]);
            });
          };

          console.log(`Starting traversal from origin hash: ${originHash}`);
          traverse(originHash, []);

          console.log(`All paths found:`, paths);
          console.log(`Total paths found:`, paths.length);
          return paths;
        },





      getSimpleLabel(baseLabel, index) {
      const isLastNumeric = !isNaN(baseLabel.slice(-1));

      if (isLastNumeric) {
        const letter = String.fromCharCode(97 + index % 26); // 'a' to 'z'
        const cycle = Math.floor(index / 26); // Handle overflow past 'z'

        // If overflow past 'z', append '.1', '.2', etc., to the last label
        if (cycle > 0) {
          return `${baseLabel}z.${index - 25}`; // 25 = 'z' is the 26th letter
        }
        return `${baseLabel}${letter}`;
      } else {
        // If base label ends with a letter, append incrementing numbers
        return `${baseLabel}${index + 1}`;
      }
    }
    ,

    extractChildHashes(continuesAs, allHashes) {
      if (!continuesAs) return []; // Return an empty array if the input is null or undefined

      // Remove the prefix "Continues as " if it exists
      const cleanedData = continuesAs.replace(/^Continues as\s*/, '');

      // Split the cleaned data into tokens by commas and optional spaces
      const tokens = cleanedData.split(/\s*,\s*/);

      // Filter tokens that exist in the known set of allHashes
      const validHashes = tokens.filter(token => allHashes.has(token)); // Use Set.has()

      console.log('Valid child hashes:', validHashes);

      return validHashes;
    },

    extractEntries(data) {
      const entries = [];
      const missingLabels = [];
      let currentEntry = [];

      data.forEach((row) => {
        const transactionHash = String(row[1] || "").trim(); // Safely handle Column B
        const isEmptyRow = row.every((cell) => !String(cell || "").trim()); // Safely handle each cell

        if (transactionHash) {
          // Start a new entry
          if (currentEntry.length > 0) {
            entries.push([...currentEntry]);
            if (!String(currentEntry[0][0] || "").trim()) {
              missingLabels.push(entries.length); // Flag missing label
            }
          }
          currentEntry = [row];
        } else if (!isEmptyRow) {
          // Add to current entry
          currentEntry.push(row);
        } else if (isEmptyRow && currentEntry.length > 0) {
          // End of the entry
          entries.push([...currentEntry]);
          if (!String(currentEntry[0][0] || "").trim()) {
            missingLabels.push(entries.length); // Flag missing label
          }
          currentEntry = [];
        }
      });

      // Add the last entry if necessary
      if (currentEntry.length > 0) {
        entries.push([...currentEntry]);
        if (!String(currentEntry[0][0] || "").trim()) {
          missingLabels.push(entries.length); // Flag missing label
        }
      }

      return { entries, missingLabels };
    },

    sortEntries(entries) {
          const parseLabel = (label) => {
            const parts = label.match(/(\d+|[a-z]+)/gi) || [];
            return parts.map((part) => (isNaN(part) ? part : parseInt(part, 10)));
          };

          return entries.sort((a, b) => {
            const labelA = parseLabel(a[0][0]);
            const labelB = parseLabel(b[0][0]);

            for (let i = 0; i < Math.max(labelA.length, labelB.length); i++) {
              if (labelA[i] === undefined) return -1;
              if (labelB[i] === undefined) return 1;
              if (labelA[i] < labelB[i]) return -1;
              if (labelA[i] > labelB[i]) return 1;
            }
            return 0;
          });
        }
};

function clone2D(data) {
  return (Array.isArray(data) ? data : []).map((row) => (Array.isArray(row) ? row.slice() : []));
}


function silenceTool68Console(options = {}) {
  if (options.debugTool68B || options.debugTool68) return null;
  const originalLog = console.log;
  const originalWarn = console.warn;
  const originalError = console.error;
  console.log = () => {};
  console.warn = () => {};
  console.error = () => {};
  return () => {
    console.log = originalLog;
    console.warn = originalWarn;
    console.error = originalError;
  };
}

function normalizeOptionBool(primary, fallback, defaultValue) {
  if (primary !== undefined) return !!primary;
  if (fallback !== undefined) return !!fallback;
  return !!defaultValue;
}

function createTool68BRunner(options = {}) {
  const requestedChain = String(options.chain || options.Chain || 'ETH').trim().toUpperCase();
  const chain = requestedChain === 'BTC' || requestedChain === 'BITCOIN' ? 'BTC' : 'ETH';

  return {
    chain,
    extraSpacing: normalizeOptionBool(options.extraSpacing, options.ExtraSpacing, false),
    baseLabel: String(options.baseLabel || options.BaseLabel || '1').trim() || '1',
    pruneBTCUnfollowed: normalizeOptionBool(options.pruneBTCUnfollowed, options.PruneBTCUnfollowed, true),
    labelLongestPath: normalizeOptionBool(
      options.labelLongestPath,
      options.LabelLongestPathFirst ?? options.labelLongestPathFirst,
      true
    ),
    duplicateCount: 0,
    notesUpdated: 0,
    tokenMismatchCount: 0,
    userFeedback: '',
    changesLog: [],
    missingLabels: [],
    hashes: new Set(),
    originHash: null,
    ...tool68BMethods
  };
}

export function processCaseBuddyTool68Rows(inputRows = [], options = {}) {
  const runner = createTool68BRunner(options);
  runner.resetStatus?.();

  let data = clone2D(inputRows);
  const restoreConsole = silenceTool68Console(options);

  try {
    if (runner.chain === 'BTC') {
      data = runner.runTool40F_All(data);
    } else {
      data = runner.runTool40D_All(data);
    }

    data = runner.runTool49B_All(data);
    data = runner.runTool46B_All(data);

    const arranged = runner.runTool50_All(data) || { data, missingLabels: [] };
    const missingLabels = Array.isArray(arranged.missingLabels) ? arranged.missingLabels : [];
    const outputRows = missingLabels.length > 0 ? data : (arranged.data || data);
    const feedbackParts = [];
    if (runner.userFeedback) feedbackParts.push(String(runner.userFeedback));
    if (missingLabels.length > 0) {
      feedbackParts.push(`Entries missing labels: ${missingLabels.length}. Please review Tool 68B output.`);
    } else {
      feedbackParts.push('✅ Workflow complete. Final output is ready.');
    }

    return {
      success: missingLabels.length === 0,
      rows: clone2D(outputRows),
      duplicateCount: Number(runner.duplicateCount) || 0,
      notesUpdated: Number(runner.notesUpdated) || 0,
      tokenMismatchCount: Number(runner.tokenMismatchCount) || 0,
      userFeedback: feedbackParts.join('\n'),
      missingLabels,
      changesLog: Array.isArray(runner.changesLog) ? runner.changesLog.slice() : []
    };
  } catch (error) {
    return {
      success: false,
      rows: clone2D(data),
      duplicateCount: Number(runner.duplicateCount) || 0,
      notesUpdated: Number(runner.notesUpdated) || 0,
      tokenMismatchCount: Number(runner.tokenMismatchCount) || 0,
      userFeedback: runner.userFeedback || '',
      missingLabels: Array.isArray(runner.missingLabels) ? runner.missingLabels.slice() : [],
      changesLog: Array.isArray(runner.changesLog) ? runner.changesLog.slice() : [],
      error: error?.message || String(error)
    };
  } finally {
    restoreConsole?.();
  }
}

export default processCaseBuddyTool68Rows;
